import { KonvaEventObject } from 'konva/lib/Node';
import { FunctionComponent, useMemo } from 'react';
import { Group, Line, Rect } from 'react-konva';
import { Color } from 'types/global';
import { getEditTypeColor } from 'utils/entity';
import { diffDay } from 'utils/time';
import { ACTIVITY_TYPE, Activity, GraphStyle, TAGS } from '../../types';
import { brighten } from '../../utils/tools';



type Props = {
	start: number,
	activity: Activity,
	highlight: boolean,
	disabled: boolean,
	onCellEnter: (e: KonvaEventObject<MouseEvent>) => void,
	onCellLeave: (e: KonvaEventObject<MouseEvent>) => void,
	onCellClick: (e: KonvaEventObject<MouseEvent>) => void,
	onCellDoubleClick: (e: KonvaEventObject<MouseEvent>) => void,
	style: GraphStyle,
}

const ActivityCmp: FunctionComponent<Props> = ({
	start,
	activity,
	highlight,
	disabled,
	onCellEnter,
	onCellLeave,
	onCellClick,
	onCellDoubleClick,
	style,
}) => {

	const length = activity.length
	const col = diffDay(activity.start, start)
	const row = activity.row

	const startX = col * style.space.x
	const startY = row * style.space.y
	const endX = (col + length - 1) * style.space.x

	const haveStart = activity.type == ACTIVITY_TYPE.START || activity.type == ACTIVITY_TYPE.FULL
	const haveEnd = activity.type == ACTIVITY_TYPE.END || activity.type == ACTIVITY_TYPE.FULL

	// get colors
	const inSession = !style.sessionHighlight || activity.tags?.[TAGS.SESSION] == style.sessionHighlight
	const { colorA, colorB, strokeColor, strokeWidth, opacity, dash } = useMemo(() =>
		getActivityColor(highlight, disabled, activity, inSession, style),
		[highlight, disabled, activity, style.activity, inSession]
	)

	// i rettangolini
	const rectY = (row * style.space.y) + style.activity.rects.marginY
	const rectWidth = style.space.x - (style.activity.rects.marginX * 2)
	const rectHeight = style.space.y - (style.activity.rects.marginY * 2)


	// HANDLERs
	const handleMouseEnter = (e: KonvaEventObject<MouseEvent>) => {
		e.cancelBubble = true
		onCellEnter(e)
	}
	const handleMouseLeave = (e: KonvaEventObject<MouseEvent>) => {
		e.cancelBubble = true
		onCellLeave(e)
	}
	const handleClick = (e: KonvaEventObject<MouseEvent>) => {
		e.cancelBubble = true
		onCellClick(e)
	}

	// RENDER
	const joint = activity.tags?.[TAGS.JOINT] ?? []
	const rectProps = {
		x: startX + style.activity.padding.x,
		y: startY + style.activity.padding.y,
		width: (length * style.space.x) - (style.activity.padding.x * 2),
		height: style.activity.padding.height,
	}

	return (
		<Group transformsEnabled="position" cache>

			<Rect transformsEnabled="position"
				{...rectProps}
				fill={colorA}
				strokeWidth={strokeWidth}
				stroke={strokeColor}
				cornerRadius={3}
				dash={dash}

				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onClick={handleClick}
				onTap={handleClick}
				onDblClick={onCellDoubleClick}
				onDblTap={onCellDoubleClick}
			/>

			{!haveStart
				&& <CapLink x={startX} y={startY} color={colorA} strokeColor={strokeColor} style={style} />
			}

			{!haveEnd
				&& <CapLink flip x={endX} y={startY} color={colorA} strokeColor={strokeColor} style={style} />
			}

			{Array.from({ length }).map((_, index) => (
				<Rect key={index} listening={false} transformsEnabled="position" perfectDrawEnabled={false}
					x={((col + index) * style.space.x) + style.activity.rects.marginX} y={rectY}
					width={rectWidth} height={rectHeight}
					fill={joint.includes(index + 1) ? (disabled ? "#8f8f8f" : "#FFFFFF") : colorB}
					opacity={opacity}
				/>
			))}

		</Group>
	)
}

export default ActivityCmp

function CapLink({
	x, y, flip, color, strokeColor, style
}: {
	x: number, y: number, flip?: boolean, color: Color, strokeColor: Color, style: GraphStyle
}) {
	const tmpX = x + style.space.x2
	const offset = style.space.x2

	return (<Group x={tmpX} y={y}
		listening={false}
		scaleX={flip ? -1 : 1}
		offsetX={offset}
	>
		<Line
			points={style.activity.capPoint}
			closed={true}
			fill={color}
		/>
		<Line
			points={style.activity.capPoint}
			closed={false}
			stroke={strokeColor}
			strokeWidth={1}
		/>
	</Group>)
}

function getActivityColor(
	highlight: boolean,
	disabled: boolean,
	activity: Activity,
	inSession: boolean,
	style: GraphStyle
) {
	const opacity = inSession ? 1 : .4

	// FILL
	let colorMain = activity.color ?? style.activity.defaultColor
	let colorA = colorMain
	if (!inSession) colorA += "65"
	if (disabled) colorA = brighten(colorA, .3)
	let colorB = brighten(colorA, .9)

	// STROKE
	const dash = null //inSession ? null : [6,6]
	let strokeColor = inSession ? style.activity.border.color : colorMain
	let strokeWidth = 1
	if ( activity.overlaps?.length > 0 ) {
		strokeColor = style.activity.overlap.color
		strokeWidth = style.activity.overlap.thickness
	}
	if (activity.tags?.[TAGS.EDIT] != null) {
		strokeWidth = 3
		strokeColor = getEditTypeColor(activity.tags?.[TAGS.EDIT])
	}
	if (highlight) {
		strokeColor = style.activity.border.highlight.color
	} else {
		//if (activity.layer?.errors?.length > 0) strokeColor = style.activity.border.warning.color
		if (disabled) strokeColor = brighten(strokeColor, .4)
	}

	return { colorA, colorB, strokeColor, strokeWidth, opacity, dash }
}